/**
 * The at-risk ratio, in one place.
 *
 * The lead service owns this number and publishes it on `/leads/config`. The
 * admin needs it in `LeadMapper`, which runs inside the data layer — before
 * any container has had a chance to read the config and pass it down — so the
 * value is cached here when the config is fetched rather than threaded through
 * seven call sites that have no other reason to know about it.
 *
 * What this replaces is a literal `0.75` sitting in `deriveSlaState` with a
 * comment admitting it mirrored the service. That was harmless only while the
 * ratio was a constant in both places; it is editable now, and a second copy
 * of an editable number is a screen that disagrees with the badge beside it.
 */

/**
 * Used until the config has loaded, and if the request fails.
 *
 * It matches `SLA_AT_RISK_RATIO` in the lead service, which is that service's
 * own fallback — so when nothing has been configured anywhere, both ends agree
 * without either having asked.
 */
export const DEFAULT_SLA_AT_RISK_RATIO = 0.75;

let atRiskRatio: number = DEFAULT_SLA_AT_RISK_RATIO;

/**
 * Called by `getLeadConfig`, which is the only writer.
 *
 * A value outside (0, 1) is ignored rather than stored: at-risk has to warn
 * before the breach and after the lead arrived, and a bad number here would
 * turn every lead in the queue amber or none of them.
 */
export function setSlaAtRiskRatio(ratio?: number): void {
  if (typeof ratio !== 'number' || !isFinite(ratio) || ratio <= 0 || ratio >= 1) return;
  atRiskRatio = ratio;
}

export function getSlaAtRiskRatio(): number {
  return atRiskRatio;
}

/** Test seam — nothing in the app resets it. */
export function resetSlaAtRiskRatio(): void {
  atRiskRatio = DEFAULT_SLA_AT_RISK_RATIO;
}
